import { useEffect } from "react";

export const SITE_URL = "https://lszhyx.cn";
export const SITE_NAME = "瓜田雷达";

export interface BreadcrumbItem {
  name: string;
  path: string;
}

interface SeoOptions {
  title: string;
  description: string;
  keywords: string;
  path: string;
  breadcrumbs?: BreadcrumbItem[];
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

function setMeta(attr: "name" | "property", key: string, content: string) {
  const selector = `meta[${attr}="${key}"]`;
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setCanonical(href: string) {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export default function useSeo(options: SeoOptions) {
  const { title, description, keywords, path, breadcrumbs, jsonLd } = options;
  const jsonLdKey = JSON.stringify(jsonLd ?? null);
  const crumbKey = JSON.stringify(breadcrumbs ?? null);

  useEffect(() => {
    document.title = title;

    setMeta("name", "description", description);
    setMeta("name", "keywords", keywords);
    setMeta("name", "robots", "index,follow");

    const url = `${SITE_URL}${path}`;
    setCanonical(url);

    setMeta("property", "og:type", "website");
    setMeta("property", "og:site_name", SITE_NAME);
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", description);
    setMeta("property", "og:url", url);

    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", description);

    const graph: Record<string, unknown>[] = [];
    if (jsonLd) {
      if (Array.isArray(jsonLd)) {
        graph.push(...jsonLd);
      } else {
        graph.push(jsonLd);
      }
    }

    const crumbList: BreadcrumbItem[] = crumbKey ? (JSON.parse(crumbKey) ?? []) : [];
    if (crumbList.length > 0) {
      graph.push({
        "@type": "BreadcrumbList",
        itemListElement: crumbList.map((item, index) => ({
          "@type": "ListItem",
          position: index + 1,
          name: item.name,
          item: `${SITE_URL}${item.path}`,
        })),
      });
    }

    let script = document.getElementById("page-jsonld") as HTMLScriptElement | null;
    if (graph.length === 0) {
      if (script) {
        script.textContent = "";
      }
      return;
    }
    if (!script) {
      script = document.createElement("script");
      script.id = "page-jsonld";
      script.type = "application/ld+json";
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@graph": graph,
    });
  }, [title, description, keywords, path, jsonLdKey, crumbKey]);
}